import { Button, makeStyles, MobileStepper, Typography } from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import React, { Children, isValidElement, ReactNode } from 'react';
import SwipeableViews from 'react-swipeable-views';

const useStyles = makeStyles(_theme => ({
  swipeBtn: {
    border: 'none',
    height: '2.25rem',
    minWidth: '2.25rem',
    padding: '0',
    verticalAlign: 'text-top',
    borderRadius: '50%',
  },
  swipeEnabled: {
    backgroundColor: '#d9eeff',
    color: '#000',
    '&:hover': {
      backgroundColor: '#d9eeff',
    },
    '&:active': {
      backgroundColor: '#d9eeff',
    },
  },
  swipeDisabled: {
    opacity: 0.5,
    color: '#000',
    background: '#e3e9ed',
    cursor: 'not-allowed',
    '& > span > svg': {
      fill: '#000',
    },
  },
  titleStyles: {
    color: '#0033A0',
    width: '100%',
    fontSize: '1rem',
    fontWeight: 600,
    textAlign: 'left',
    marginBottom: '1rem',
    paddingLeft: '0.15rem',
  },
  stepper: {
    marginTop: '1.25rem',
    boxShadow: 'none',

    '& > .MuiMobileStepper-dots': {
      border: '1px solid #ccc',
      borderRadius: '6.25rem',
      backgroundColor: '#f1f1f1',
      padding: '0.75rem',
      '& > .MuiMobileStepper-dot': {
        margin: '0 0.15rem',
        backgroundColor: '#0033A0',
      },
      '& > .MuiMobileStepper-dotActive': {
        backgroundColor: '#45bce5',
      },
    },
  },
}));

type NextElementInfo = {
  label: string;
  text: string;
};

type SwipeStyles = Partial<{
  swipe: string;
  stepper: string;
  footer: string;
  nextElementInfo: string;
}>;

type SwipeableViewProps = {
  children: ReactNode;
  active?: number;
  styles?: SwipeStyles;
  nextElementInfo?: NextElementInfo;
  title?: string;
  onSwipe?: (activeIndex: number) => void;
};

export const getValidChildren = (children: ReactNode) =>
  Children.toArray(children).filter(child => isValidElement(child));

export const SwipeableView = (props: SwipeableViewProps) => {
  const {active, children, styles, title, nextElementInfo, onSwipe} = props;
  const [activeStep, setActiveStep] = React.useState(active ?? 0);
  const {stepper, swipeBtn, swipeDisabled, swipeEnabled, titleStyles} = useStyles();

  const handleNext = () => {
    setActiveStep(prevActive => prevActive + 1);
    onSwipe?.(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActive => prevActive - 1);
    onSwipe?.(activeStep - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
    onSwipe?.(step);
  };

  const swipeableChildren = getValidChildren(children);
  const totalItems = swipeableChildren.length;

  return (
    <>
      {title && (
        <div className={titleStyles}>
          <span>{title}</span>
        </div>
      )}

      <SwipeableViews
        index={activeStep}
        id="swipeable-container"
        onChangeIndex={handleStepChange}
        axis="x"
        enableMouseEvents
        className={styles?.swipe ?? ''}
      >
        {swipeableChildren}
      </SwipeableViews>
      {totalItems > 1 && (
        <div className={`${styles?.footer ?? ''}`}>
          <MobileStepper
            steps={totalItems}
            position="static"
            variant="dots"
            activeStep={activeStep}
            className={`${styles?.stepper} ${stepper}`}
            nextButton={
              <Button
                aria-label="Swipe Next"
                className={`${swipeBtn} ${activeStep === totalItems - 1 ? swipeDisabled : swipeEnabled}`}
                disabled={activeStep === totalItems - 1}
                id="swipe-next-btn"
                size="small"
                onClick={handleNext}
              >
                {<KeyboardArrowRight />}
              </Button>
            }
            backButton={
              <Button
                className={`${swipeBtn} ${activeStep === 0 ? swipeDisabled : swipeEnabled}`}
                aria-label="Swipe Previous"
                disabled={activeStep === 0}
                id="swipe-prev-btn"
                size="small"
                onClick={handleBack}
              >
                {<KeyboardArrowLeft />}
              </Button>
            }
          />

          {nextElementInfo && (
            <Typography component="div" className={`${styles?.nextElementInfo}`}>
              <span style={{fontWeight: 700}}>{nextElementInfo.label}</span>
              <span>{activeStep !== totalItems - 1 ? nextElementInfo.text : 'N/A'}</span>
            </Typography>
          )}
        </div>
      )}
    </>
  );
};
